import type { ChangeEvent, SyntheticEvent } from "react";
import { useEffect, useId, useRef, useState } from "react";

import { useCategoriesAndThemes } from "@/hooks/useCategoriesAndThemes";
import type { GeneratePreviewsRequest } from "@/types/previews";

export interface GeneratePreviewDialogProps {
  readonly open: boolean;
  onGenerate(req: GeneratePreviewsRequest): void;
  onCancel(): void;
}

// CR-110: native <dialog> picker for ad-hoc theme preview generation.
// Same shell as ConfirmDialog — showModal/close via a ref and useId for aria.
// Category → Theme cascade; submit disabled until both are chosen; Force
// defaults to off per reviewer consensus (synthesis Q3).
export function GeneratePreviewDialog({
  open,
  onGenerate,
  onCancel,
}: GeneratePreviewDialogProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();
  const categoryId = useId();
  const themeId = useId();
  const forceId = useId();
  const themesHintId = useId();

  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedThemeSlug, setSelectedThemeSlug] = useState<string>("");
  const [force, setForce] = useState(false);

  // Load catalog data via the shared hook; passing null skips the themes fetch.
  const { categories, themes, loadingCategories, loadingThemes, error } =
    useCategoriesAndThemes(selectedCategory || null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  // Reset transient selection when the dialog transitions closed → open.
  useEffect(() => {
    if (open) {
      setSelectedCategory("");
      setSelectedThemeSlug("");
      setForce(false);
    }
  }, [open]);

  // When the category changes, drop the theme selection.
  const handleCategoryChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    setSelectedCategory(e.target.value);
    setSelectedThemeSlug("");
  };

  const handleNativeCancel = (event: SyntheticEvent<HTMLDialogElement>): void => {
    // Prevent native close so parent `open` drives state (same pattern as ConfirmDialog).
    event.preventDefault();
    onCancel();
  };

  const canSubmit =
    selectedCategory !== "" && selectedThemeSlug !== "" && !loadingThemes;

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    onGenerate({ theme_id: selectedThemeSlug, force });
  };

  const showEmptyThemesHint =
    selectedCategory !== "" && !loadingThemes && themes.length === 0;

  return (
    <dialog
      ref={ref}
      className="generate-preview-dialog confirm-dialog"
      role="dialog"
      aria-labelledby={titleId}
      aria-describedby={descId}
      onCancel={handleNativeCancel}
    >
      <div className="confirm-dialog__content">
        <h2 id={titleId} className="confirm-dialog__title">
          Generate preview
        </h2>
        <p id={descId} className="confirm-dialog__message">
          Pick a category and theme to generate a fresh preview image for this
          theme only.
        </p>

        {error ? (
          <div className="error-banner" role="alert">
            <div className="error-banner__content">
              <div className="error-banner__title">Catalog load failed</div>
              <div className="error-banner__message">{error.message}</div>
            </div>
          </div>
        ) : null}

        <div className="generate-preview-dialog__fields">
          <label
            htmlFor={categoryId}
            className="generate-preview-dialog__field"
          >
            <span className="generate-preview-dialog__label">Category</span>
            <select
              id={categoryId}
              className="generate-preview-dialog__select"
              value={selectedCategory}
              onChange={handleCategoryChange}
              disabled={loadingCategories}
            >
              <option value="">
                {loadingCategories ? "Loading…" : "Select a category"}
              </option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <label htmlFor={themeId} className="generate-preview-dialog__field">
            <span className="generate-preview-dialog__label">Theme</span>
            <select
              id={themeId}
              className="generate-preview-dialog__select"
              value={selectedThemeSlug}
              onChange={(e) => setSelectedThemeSlug(e.target.value)}
              disabled={selectedCategory === "" || loadingThemes}
              aria-describedby={showEmptyThemesHint ? themesHintId : undefined}
            >
              <option value="">
                {selectedCategory === ""
                  ? "Pick a category first"
                  : loadingThemes
                  ? "Loading…"
                  : "Select a theme"}
              </option>
              {themes.map((t) => (
                <option key={t.id} value={t.slug}>
                  {t.name}
                </option>
              ))}
            </select>
            {showEmptyThemesHint ? (
              <span
                id={themesHintId}
                className="generate-preview-dialog__hint"
              >
                No active themes in this category.
              </span>
            ) : null}
          </label>

          <label
            htmlFor={forceId}
            className="generate-preview-dialog__force"
          >
            <input
              id={forceId}
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
            />
            <span>Force regenerate (ignore staleness cache)</span>
          </label>
        </div>

        <div className="confirm-dialog__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            Generate
          </button>
        </div>
      </div>
    </dialog>
  );
}
